import { useNavigate, useParams } from "react-router";
import CalculatorGrid from "./CalculatorGrid";
import Breadcrumb, { Crumb } from "../../../../components/common/Breadcrumbs";
import { ChangeEvent, FC, FormEvent, useEffect, useState } from "react";
import AuditableInfoButton from "../../../../components/common/form/AuditableInfoButton";
import {
    useCreateTaxRoundingConfigMutation,
    useDeleteTaxRoundingConfigByIdMutation,
    useGetTaxRoundingConfigByIdQuery,
    useUpdateTaxRoundingConfigMutation
} from "../taxRoundingConfigApi";
import Form from "../../../../components/common/form/Form";
import InputField from "../../../../components/common/form/InputField";
import SelectField from "../../../../components/common/form/SelectField";
import { createOptionListForSelectTag } from "../../../../helpers/utils/Utils";
import ButtonTray from "../../../../components/common/form/ButtonTray";
import { handleRTKQuery } from "../../../../helpers/utils/RtkQueryUtils";
import { Routes as AppRoutes } from "../../../../routes";
import { OptionType } from "../../../../helpers/utils/StyleUtils";
import { useGetEnumsQuery } from "../../../../app/globalApi";
import { booleanDrop } from "../../../../helpers/constants";
import ModalButton from "../../../../components/common/form/ModalButton";
import IconButton from "../../../../components/common/form/IconButton";
import { FaTrash } from "react-icons/fa6";
import ConfirmationModal from "../../../../components/layout/modal/ConfirmationModal";
import { hasPermission } from "../../../../helpers/utils/AccessControlUtils";


interface props {
    crumbs: Crumb[];
    title: string,
    btnText: string,
    isReadOnly?: boolean
}

const TaxRoundingConfigFields: FC<props> = ({ crumbs, title, btnText, isReadOnly }) => {

    const navigate = useNavigate();
    let { roundConfigId } = useParams();

    const { data: roundConfigDataById } = useGetTaxRoundingConfigByIdQuery(roundConfigId);
    const [roundConfigData, setroundConfigData] = useState<any>({});
    const [creatRoundConfig, createRoundConfigApiResponse] = useCreateTaxRoundingConfigMutation();
    const [updateRoundConfig, updateRoundConfigpiResponse] = useUpdateTaxRoundingConfigMutation();
    const { data: enums } = useGetEnumsQuery("");
    const [deleteApi, { isLoading }] = useDeleteTaxRoundingConfigByIdMutation();

    // Inside your component
    useEffect(() => {
        if (roundConfigDataById) {
            setroundConfigData({
                ...roundConfigDataById,
                roundBase: roundConfigDataById?.roundBase ? {
                    label: roundConfigDataById.roundBase,
                    value: roundConfigDataById.roundBase
                } : null,
                roundPenalty: roundConfigDataById?.roundPenalty ? {
                    label: roundConfigDataById.roundPenalty,
                    value: roundConfigDataById.roundPenalty
                } : null,
                roundInterest: roundConfigDataById?.roundInterest ? {
                    label: roundConfigDataById.roundInterest,
                    value: roundConfigDataById.roundInterest
                } : null,
                roundSubTotal: roundConfigDataById?.roundSubtotal ? {
                    label: roundConfigDataById.roundSubtotal,
                    value: roundConfigDataById.roundSubtotal
                } : null,
                roundAttorneyFee: roundConfigDataById?.roundAttorneyFee ? {
                    label: roundConfigDataById.roundAttorneyFee,
                    value: roundConfigDataById.roundAttorneyFee
                } : null,
                roundFinalResult: roundConfigDataById?.roundFinalResult ? {
                    label: roundConfigDataById.roundFinalResult,
                    value: roundConfigDataById.roundFinalResult
                } : null,
                useIntegerArithmetic: {
                    label: roundConfigDataById?.useIntegerArithmetic ? "True" : "False",
                    value: roundConfigDataById?.useIntegerArithmetic ? "true" : "false"
                }
            });
        }
    }, [roundConfigDataById]);

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        let commonObj = {
            id: roundConfigId || "",  // For create, keep empty or undefined
            name: roundConfigData?.name || "",
            roundBase: roundConfigData?.roundBase?.value || null,
            roundPenalty: roundConfigData?.roundPenalty?.value || null,
            roundInterest: roundConfigData?.roundPenalty?.value || null, // you're using roundPenalty for interest
            roundSubtotal: roundConfigData?.roundSubTotal?.value || null,
            roundAttorneyFee: roundConfigData?.roundAttorneyFee?.value || null,
            roundFinalResult: roundConfigData?.roundFinalResult?.value || null,
            useIntegerArithmetic: roundConfigData?.useIntegerArithmetic?.value === "true"
        };

        const createOrUpdate = async () => {
            if (btnText === "Create") {
                return await creatRoundConfig(commonObj).unwrap();
            } else {
                return await updateRoundConfig({
                    id: roundConfigId,
                    requestBody: commonObj,
                }).unwrap();
            }
        };

        await handleRTKQuery(createOrUpdate, (response: any) => {
            if (response?.success || response?.data?.success) {
                setroundConfigData({});
                navigate(AppRoutes.TaxRoundingConfig.path);
            }
        });

    }

    const handleOnChange = (e: ChangeEvent<HTMLInputElement> | { name: string; value: OptionType | null }) => {
        const { name, value } = "target" in e ? e.target : e;
        setroundConfigData({
            ...roundConfigData,
            [name]: value
        })
    }
    const input = {
        id: roundConfigData?.id,
        name: roundConfigData?.name,
        roundBase: roundConfigData?.roundBase,
        roundPenalty: roundConfigData?.roundPenalty,
        roundInterest: roundConfigData?.roundInterest,
        roundSubtotal: roundConfigData?.roundSubtotal,
        roundAttorneyFee: roundConfigData?.roundAttorneyFee,
        roundFinalResult: roundConfigData?.roundFinalResult,
        useIntegerArithmetic: roundConfigData?.useIntegerArithmetic,
    };

    const handleDelete = async () => {
        if (roundConfigId) {
            await deleteApi(roundConfigId).unwrap();
        }
        navigate(AppRoutes.TaxRoundingConfig.path)
    };

    return (
        <>
            <div className="px-4 py-2">
                <div className={"justify-between sm:block md:flex lg:flex"}>
                    <Breadcrumb crumbs={crumbs} />
                    <div className={"justify-between sm:block md:flex lg:flex"}>
                        {roundConfigId && hasPermission(["TAXRCONF-D"]) && (
                            <>
                                <ModalButton
                                    triggerButton={
                                        <IconButton
                                            icon={<FaTrash />}
                                            title="Delete Tax Rounding Config"
                                            hasBackground={true}
                                            className="mr-2"
                                        />
                                    }
                                    modal={
                                        <ConfirmationModal
                                            show={true}
                                            setShow={() => {
                                            }}
                                            title="Delete Tax Rounding Config"
                                            bodyMessage="Are you sure you want to delete this Tax Rounding Config?"
                                            onSubmit={() => handleDelete()}
                                            isLoading={isLoading}
                                        />
                                    }
                                />
                                {hasPermission(["AUDIT-V"]) &&
                                    <AuditableInfoButton obj={roundConfigData} />}
                            </>
                        )}

                    </div>
                </div>
                <div className="flex justify-center mt-3 text-sm">
                    <div className="bg-text-0 lg:w-4/6 sm:w-full p-5 rounded-md shadow-[0_0_10px_-2px_rgba(0,0,0,0.2)]">
                        <h3 className="text-xl font-semibold text-surface-0 pb-3 border-b border-surface-5">{title}</h3>
                        <Form onSubmit={handleSubmit} fields={<>
                            <div className="grid grid-cols-12">
                                <InputField
                                    name={"name"}
                                    label="Name"
                                    onChange={handleOnChange}
                                    value={roundConfigData?.name || ""}
                                    type="text"
                                    placeholder="Enter Name..."
                                    required={true}
                                    disabled={isReadOnly}
                                />
                                <SelectField
                                    placeholder='Select Round Base...'
                                    value={roundConfigData?.roundBase}
                                    options={createOptionListForSelectTag(enums?.["RoundingType"], "value", "constant")}
                                    onChange={(selectedOption) => {
                                        handleOnChange({ name: "roundBase", value: selectedOption })
                                    }}
                                    label={"Round Base"}
                                    required={true}
                                    isDisabled={isReadOnly}
                                />
                                <SelectField
                                    placeholder='Select Round Penalty...'
                                    value={roundConfigData?.roundPenalty}
                                    options={createOptionListForSelectTag(enums?.["RoundingType"], "value", "constant")}
                                    onChange={(selectedOption) => {
                                        handleOnChange({ name: "roundPenalty", value: selectedOption })
                                    }}
                                    label={"Round Interest"}
                                    required={true}
                                    isDisabled={isReadOnly}
                                />
                                <SelectField
                                    placeholder='Select Round Subtotal...'
                                    value={roundConfigData?.roundSubTotal}
                                    options={createOptionListForSelectTag(enums?.["RoundingType"], "value", "constant")}
                                    onChange={(selectedOption) => {
                                        handleOnChange({ name: "roundSubTotal", value: selectedOption })
                                    }}
                                    label={"Round Subtotal"}
                                    required={true}
                                    isDisabled={isReadOnly}
                                />
                                <SelectField
                                    placeholder='Select Round Attorney Fee...'
                                    value={roundConfigData?.roundAttorneyFee}
                                    options={createOptionListForSelectTag(enums?.["RoundingType"], "value", "constant")}
                                    onChange={(selectedOption) => {
                                        handleOnChange({ name: "roundAttorneyFee", value: selectedOption })
                                    }}
                                    label={"Round Attorney Fee"}
                                    required={true}
                                    isDisabled={isReadOnly}
                                />
                                <SelectField
                                    placeholder='Select Round Final Result...'
                                    value={roundConfigData?.roundFinalResult}
                                    options={createOptionListForSelectTag(enums?.["RoundingType"], "value", "constant")}
                                    onChange={(selectedOption) => {
                                        handleOnChange({ name: "roundFinalResult", value: selectedOption })
                                    }}
                                    label={"Round Final Result"}
                                    required={true}
                                    isDisabled={isReadOnly}
                                />
                                <SelectField
                                    placeholder='Select Use Integer Arithmetic...'
                                    value={roundConfigData?.useIntegerArithmetic}
                                    options={createOptionListForSelectTag(booleanDrop, "label", "value")}
                                    onChange={(selectedOption) => {
                                        handleOnChange({ name: "useIntegerArithmetic", value: selectedOption })
                                    }}
                                    label={"Use Integer Arithmetic"}
                                    isDisabled={isReadOnly}
                                />
                            </div>
                            {!isReadOnly &&
                                <div className="col-span-12 md:grid md:grid-cols-12 gap-3">
                                    <div className="col-span-3 sm:mt-2 lg:mt-0 mt-2 flex items-center">

                                    </div>
                                    <div className="col-span-9 mt-3 flex">
                                        <ButtonTray
                                            buttons={[
                                                {
                                                    buttonProps: {
                                                        btnText: "Cancel",
                                                        onClick: () => navigate(AppRoutes.TaxRoundingConfig.path),
                                                        type: "close",
                                                    },
                                                    buttonType: "button"
                                                },
                                                {
                                                    buttonProps: {
                                                        btnText: "Submit",
                                                        type: "submit",
                                                        isLoading: (createRoundConfigApiResponse.isLoading || updateRoundConfigpiResponse.isLoading),

                                                    },
                                                    buttonType: "button",

                                                }
                                            ]}
                                            align="center"
                                            gap={2}
                                        />
                                    </div>

                                </div>
                            }

                        </>}
                        />
                    </div>
                </div>
            </div>
            <CalculatorGrid taxRoundingConfiguration={input} />
        </>
    )
}
export default TaxRoundingConfigFields;